<template>
  <div
    class="modal fade"
    id="modalTaxInvoice"
    ref="modal"
    data-bs-backdrop="static"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
      <div class="modal-content border-0 shadow-lg" style="border-radius: 16px;">
        <div class="modal-header border-0 pb-2">
          <h5 class="modal-title fw-bold text-theme">
            <i class="fa-solid fa-file-invoice me-2"/>
            {{ $t('Issue Tax Invoice') }}
            <span class="text-muted ms-1">#{{ bill.bill_no }}</span>
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"/>
        </div>

        <div class="modal-body px-4 pb-4">
          <div class="card bg-light border-0 rounded-3 mb-3">
            <div class="card-body p-3 d-flex justify-content-between align-items-center">
              <div>
                <div class="text-muted small">{{ $t('Bill Total') }}</div>
                <div class="fs-5 fw-bold">
                  {{ $storeIndex.formatThaiCurrency(billTotal) }}
                </div>
                <div v-if="shopTax.tax_id" class="small mt-1 text-muted">
                  {{ $t('Tax ID') }}: {{ shopTax.tax_id }}
                  <span v-if="bill.branch_no"> ({{ $t('Branch No') }} {{ bill.branch_no }})</span>
                </div>
              </div>
              <i class="fa-solid fa-file-invoice fa-2x text-theme opacity-50"/>
            </div>
          </div>

          <div class="text-muted small mb-2">
            {{ $t('Customer information for the tax invoice') }}
          </div>

          <div class="row g-3">
            <div class="col-md-6">
              <label class="option-card h-100" :class="{ 'is-checked': form.customer_type === 'juristic' }">
                <div class="form-check mb-0">
                  <input
                    class="form-check-input shadow-none"
                    type="radio"
                    value="juristic"
                    v-model="form.customer_type"
                    id="customerTypeJuristic"
                  >
                  <span class="form-check-label fw-medium">
                    <i class="fa-solid fa-building me-1"/>{{ $t('Juristic person') }}
                  </span>
                </div>
                <div class="text-secondary small mt-1">
                  {{ $t('Company / partnership — requires head office or branch number.') }}
                </div>
              </label>
            </div>
            <div class="col-md-6">
              <label class="option-card h-100" :class="{ 'is-checked': form.customer_type === 'individual' }">
                <div class="form-check mb-0">
                  <input
                    class="form-check-input shadow-none"
                    type="radio"
                    value="individual"
                    v-model="form.customer_type"
                    id="customerTypeIndividual"
                  >
                  <span class="form-check-label fw-medium">
                    <i class="fa-solid fa-user me-1"/>{{ $t('Individual') }}
                  </span>
                </div>
                <div class="text-secondary small mt-1">
                  {{ $t('Uses the national ID number — no branch.') }}
                </div>
              </label>
            </div>
            <div class="col-md-6">
              <label class="form-label fw-semibold small">
                {{ $t('Customer Name') }}
                <span class="text-danger">*</span>
              </label>
              <div class="custom-input-group input-group input-group-sm">
                <input
                  type="text"
                  class="form-control"
                  :class="v$.form.customer_name.$error ? 'is-invalid' : ''"
                  v-model.trim="v$.form.customer_name.$model"
                  maxlength="255"
                  :placeholder="$t('Ex: ABC Co., Ltd.')"
                >
              </div>
              <div v-for="error of v$.form.customer_name.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label fw-semibold small">
                {{ $t('Customer Tax ID') }}
                <span class="text-danger">*</span>
              </label>
              <div class="custom-input-group input-group input-group-sm">
                <input
                  type="text"
                  inputmode="numeric"
                  maxlength="13"
                  class="form-control"
                  :class="v$.form.customer_tax_id.$error ? 'is-invalid' : ''"
                  v-model.trim="v$.form.customer_tax_id.$model"
                  :placeholder="'0123456789012'"
                >
              </div>
              <div class="form-text">
                {{ form.customer_type === 'juristic'
                  ? $t('13-digit juristic person registration number.')
                  : $t('13-digit national ID number.') }}
              </div>
              <div v-for="error of v$.form.customer_tax_id.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
            <div class="col-md-6" v-if="form.customer_type === 'juristic'">
              <label class="form-label fw-semibold small">
                {{ $t('Customer Branch No') }}
                <span class="text-danger">*</span>
              </label>
              <div class="custom-input-group input-group input-group-sm">
                <input
                  type="text"
                  inputmode="numeric"
                  maxlength="5"
                  class="form-control"
                  :class="v$.form.customer_branch.$error ? 'is-invalid' : ''"
                  v-model.trim="v$.form.customer_branch.$model"
                  :placeholder="'00000'"
                >
              </div>
              <div class="form-text">
                {{ $t('00000 = Head office') }}
              </div>
              <div v-for="error of v$.form.customer_branch.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label fw-semibold small">
                {{ $t('Zipcode') }}
                <span class="text-danger">*</span>
              </label>
              <div class="custom-input-group input-group input-group-sm">
                <input
                  type="text"
                  inputmode="numeric"
                  maxlength="5"
                  class="form-control"
                  :class="v$.form.postal_code.$error ? 'is-invalid' : ''"
                  v-model.trim="v$.form.postal_code.$model"
                  :placeholder="$t('Enter zipcode') + '...'"
                >
              </div>
              <div class="form-text">
                {{ $t('Fill in the zipcode to choose sub-district, district and province.') }}
              </div>
              <div v-for="error of v$.form.postal_code.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
            <div class="col-12">
              <label class="form-label fw-semibold small">
                {{ $t('Address') }}
                <span class="text-danger">*</span>
              </label>
              <div class="custom-input-group input-group input-group-sm">
                <textarea
                  class="form-control"
                  rows="2"
                  :class="v$.form.address.$error ? 'is-invalid' : ''"
                  v-model.trim="v$.form.address.$model"
                  :placeholder="$t('Enter address') + '...'"
                />
              </div>
              <div v-for="error of v$.form.address.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-semibold small">
                {{ $t('Sub District') }}
                <span class="text-danger">*</span>
              </label>
              <div class="custom-input-group input-group input-group-sm">
                <select2
                  style="width: 100%;"
                  v-model="v$.form.sub_district.$model"
                  :class="v$.form.sub_district.$error ? 'is-invalid' : ''"
                  :options="districts"
                  :settings="select2FormAddress.setting"
                  @select="selectAddresses"
                />
              </div>
              <div v-for="error of v$.form.sub_district.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-semibold small">
                {{ $t('District') }}
                <span class="text-danger">*</span>
              </label>
              <div class="custom-input-group input-group input-group-sm">
                <select2
                  style="width: 100%;"
                  v-model="v$.form.district.$model"
                  :class="v$.form.district.$error ? 'is-invalid' : ''"
                  :options="amphoes"
                  :settings="select2FormAddress.setting"
                  @select="selectAddresses"
                />
              </div>
              <div v-for="error of v$.form.district.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-semibold small">
                {{ $t('Province') }}
                <span class="text-danger">*</span>
              </label>
              <div class="custom-input-group input-group input-group-sm">
                <select2
                  style="width: 100%;"
                  v-model="v$.form.province.$model"
                  :class="v$.form.province.$error ? 'is-invalid' : ''"
                  :options="provinces"
                  :settings="select2FormAddress.setting"
                  @select="selectAddresses"
                />
              </div>
              <div v-for="error of v$.form.province.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>

          <div class="d-flex gap-2 mt-4">
            <button
              type="button"
              class="btn btn-outline-secondary flex-grow-1 py-2 fw-bold rounded-3"
              data-bs-dismiss="modal"
            >
              {{ $t('Cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-primary flex-grow-1 py-2 fw-bold rounded-3"
              :disabled="v$.$invalid || submitting"
              @click="confirm"
            >
              <i v-if="submitting" class="fa-solid fa-spinner fa-spin me-2"/>
              <i v-else class="fa-solid fa-file-invoice me-2"/>
              {{ $t('Issue Tax Invoice') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation-tax-invoice";
import Select2 from 'vue3-select2-component';
import { searchAddressByZipcode } from 'thai-address-database';

export default {
  name: 'ModalTaxInvoice',
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validation(this.form)
  },
  components: { Select2 },
  props: {
    bill: { type: Object, required: true },
    // ค่าภาษีของร้าน (filter.shop_tax) — เลขผู้เสียภาษีเป็นของนิติบุคคล ไม่ใช่ของสาขา
    shopTax: { type: Object, default: () => ({}) }
  },
  emits: ['close', 'confirm'],
  data() {
    return {
      submitting: false,
      select2FormAddress: {
        setting: {
          minimumResultsForSearch: Infinity,
          dropdownParent: '#modalTaxInvoice'
        }
      },
      form: {
        // FE-only — คุมว่าต้องกรอกสาขาไหม (บุคคลธรรมดาไม่มีสาขา) ไม่ได้ส่งขึ้น API
        customer_type: 'juristic',
        customer_name: this.bill.customer_name || '',
        customer_tax_id: '',
        // 00000 = สำนักงานใหญ่ (ค่าเริ่มต้นที่พบบ่อยสุด)
        customer_branch: '00000',
        address: '',
        // '0' = ยังไม่ได้เลือก (ตรงกับ option แรกของ select2 เหมือนหน้าสร้างลูกค้า)
        sub_district: '0',
        district: '0',
        province: '0',
        postal_code: ''
      }
    };
  },
  computed: {
    billTotal() {
      const n = parseFloat(this.bill.total);
      return Number.isFinite(n) ? n : 0;
    },
    // ที่อยู่จากเลขไปรษณีย์ (แพทเทิร์นเดียวกับ customers/OffcanvasForm.vue)
    addresses() {
      return searchAddressByZipcode(this.form.postal_code, 100);
    },
    districts() {
      if (!this.addresses.find((addresse) => addresse.district == this.form.sub_district)) {
        this.form.sub_district = '0';
      }
      return [
        { id: '0', text: this.$t('Select') + '...', module: 'sub_district' }
      ].concat([...new Set(this.addresses.map(item => item.district))]
        .map(item => ({ id: item, text: item, module: 'sub_district' })));
    },
    amphoes() {
      if (!this.addresses.find((addresse) => addresse.amphoe == this.form.district)) {
        this.form.district = '0';
      }
      return [
        { id: '0', text: this.$t('Select') + '...', module: 'district' }
      ].concat([...new Set(this.addresses.map(item => item.amphoe))]
        .map(item => ({ id: item, text: item, module: 'district' })));
    },
    provinces() {
      if (!this.addresses.find((addresse) => addresse.province == this.form.province)) {
        this.form.province = '0';
      }
      return [
        { id: '0', text: this.$t('Select') + '...', module: 'province' }
      ].concat([...new Set(this.addresses.map(item => item.province))]
        .map(item => ({ id: item, text: item, module: 'province' })));
    }
  },
  watch: {
    // สลับประเภทลูกค้า → บุคคลธรรมดาไม่มีสาขา ล้างค่าทิ้งไม่ให้ติดไปกับ payload
    'form.customer_type'(type) {
      this.form.customer_branch = type === 'juristic' ? '00000' : '';
    }
  },
  mounted() {
    $(this.$refs.modal).modal('show');
    $(this.$refs.modal).on('hidden.bs.modal', () => this.$emit('close'));
  },
  methods: {
    // เลือกจาก select2 ตัวใดตัวหนึ่ง → เติมระดับที่เหลือให้อัตโนมัติ
    selectAddresses({ id, module }) {
      if (module == 'sub_district') {
        if (id != '0') {
          let address = this.addresses.find((addresse) => addresse.district == id);
          this.form.district = address.amphoe;
          this.form.province = address.province;
          this.form.postal_code = address.zipcode;
        }
      }

      if (module == 'district') {
        this.form.sub_district = '0';
        if (id != '0') {
          let address = this.addresses.find((addresse) => addresse.amphoe == id);
          this.form.province = address.province;
          this.form.postal_code = address.zipcode;
        }
      }

      if (module == 'province') {
        this.form.sub_district = '0';
        this.form.district = '0';
        if (id != '0') {
          let address = this.addresses.find((addresse) => addresse.province == id);
          this.form.postal_code = address.zipcode;
        }
      }
    },
    async confirm() {
      const valid = await this.v$.$validate();
      if (!valid || this.submitting) return;
      this.submitting = true;

      // Drop empty optional fields; only send customer_id when the bill is
      // linked to a member (backend autofills from the customer record).
      // '0' = ยังไม่ได้เลือกใน select2 → ไม่ส่งขึ้น API
      const payload = { customer_name: this.form.customer_name.trim() };
      if (this.bill.customer_id) payload.customer_id = this.bill.customer_id;
      ['customer_tax_id', 'customer_branch', 'address', 'sub_district', 'district', 'province', 'postal_code']
        .forEach(k => {
          const v = (this.form[k] || '').trim();
          if (v && v !== '0') payload[k] = v;
        });

      this.$emit('confirm', payload);
    },
    hide() {
      $(this.$refs.modal).modal('hide');
    },
    stopSubmitting() {
      this.submitting = false;
    }
  }
};
</script>
